const URL_ID = /url\(\s*#([^)]+?)\s*\)/gi;

export interface PreparedGlassArtwork {
  viewBox: string;
  /** Full standalone SVG document for `<image href>` (blob URL). */
  paintSvg: string;
  /** Object URL created by the loader hook. */
  href?: string;
}

function parseViewBox(root: Element): string | null {
  const authored = root.getAttribute('viewBox')?.trim();
  if (authored) return authored;
  const width = Number.parseFloat(String(root.getAttribute('width') ?? ''));
  const height = Number.parseFloat(String(root.getAttribute('height') ?? ''));
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return `0 0 ${width} ${height}`;
  }
  return null;
}

function serializeSvg(root: Element): string {
  const serialized = new XMLSerializer().serializeToString(root);
  return serialized.includes('xmlns=')
    ? serialized
    : serialized.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
}

function rewriteUrlReferences(root: Element, idMap: Map<string, string>): void {
  root.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const value = attribute.value;
      if (!value.includes('url(#')) return;
      attribute.value = value.replace(URL_ID, (_match, id: string) => {
        const next = idMap.get(id.trim());
        return next ? `url(#${next})` : _match;
      });
    });
  });
}

function prefixIds(root: Element, prefix: string): Map<string, string> {
  const idMap = new Map<string, string>();
  root.querySelectorAll('[id]').forEach((element) => {
    const id = element.getAttribute('id')?.trim();
    if (!id) return;
    const next = `${prefix}-${id}`;
    idMap.set(id, next);
    element.setAttribute('id', next);
  });
  rewriteUrlReferences(root, idMap);
  return idMap;
}

/**
 * Parse reusable glass SVG for painting through `<image href>`.
 * Gradient/pattern ids are prefixed so multiple doors can share one page.
 */
export function prepareGlassArtwork(svgText: string, idPrefix: string): PreparedGlassArtwork | null {
  const document = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (document.querySelector('parsererror')) return null;
  const root = document.documentElement;
  if (root.tagName.toLowerCase() !== 'svg') return null;
  const viewBox = parseViewBox(root);
  if (!viewBox) return null;

  prefixIds(root, idPrefix);
  root.querySelectorAll('namedview, sodipodi\\:namedview').forEach((node) => node.remove());

  return {
    viewBox,
    paintSvg: serializeSvg(root),
  };
}
